import React from 'react';
import { FormText } from '~/components/FormControl';
import { Form, Col, Row } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { API_PREFIX } from '../../characterClass.api';
import { checkDuplicateName } from '~/utils/validators.yup';
import { useFormTab } from '~/pages/CardSaga/components/providers/FormTabProvider';
import isEmpty from 'lodash/isEmpty';
import { FormCombo } from '~/components/FormControl';
import { FormNumber, FormSelect, FormRadio, Fieldset } from '~/components/FormControl';
import snakeCase from 'lodash/snakeCase';
import { useSelector } from 'react-redux';
import CLAPreviewPictureWithHOC from './CLAPreviewPictureWithHOC';
import DescriptionAndClassBonus from './DescriptionAndClassBonus';
import PreviewAsImg from '~/components/FileUploaded/picture-preview/PreviewAsImg';
import { formLabel } from '~/utils/helpers';
import { useLocation } from 'react-router-dom';

function CardInformation({ values }) {
    const { resetCount, cardMetaData, initialValues } = useFormTab();
    const { actions } = cardMetaData;
    const { characterAndCardPictures, bonusConditions } = useSelector((state) => state.cardSaga.characterClassCard);
    const asyncApiCall = checkDuplicateName(
        initialValues.name,
        (() => {
            const arr = API_PREFIX.split('/');
            arr[arr.length - 1] = snakeCase(arr.at(-1));

            return arr.join('/');
        })(),
    );

    //React change view and then scroll to element: https://stackoverflow.com/a/71975021/11297747
    const { state } = useLocation();
    const { targetId } = state || {};
    React.useEffect(() => {
        const el = document.getElementById(targetId);
        if (el) {
            el.scrollIntoView();
        }
    }, [targetId]);

    return (
        <>
            {!isEmpty(cardMetaData) && (
                <>
                    <FormText
                        sm="2"
                        name="name"
                        className="offset-sm-5"
                        isLabelHidden
                        asyncApiCall={asyncApiCall}
                    />
                    <Fieldset
                        sm={12}
                        fieldsetStyle={{ columnGap: '2rem' }}
                    >
                        <Fieldset.Legend>Pictures</Fieldset.Legend>
                        <Row className="mt-4">
                            {characterAndCardPictures.firstRow.map((item, i) => (
                                <Col
                                    className={'d-flex flex-column align-items-center justify-content-center'}
                                    style={{ gap: 10 }}
                                    key={i}
                                >
                                    {item.editable === true ? (
                                        <CLAPreviewPictureWithHOC
                                            pictureName={item.name}
                                            pictureVal={values[item.name]}
                                            resetCount={resetCount}
                                            width={item.width}
                                            maxWidth={item.maxWidth}
                                            height={item.height}
                                            heightWrapper={item.heightWrapper}
                                            wrapperClass="d-flex flex-column align-items-center justify-content-center"
                                            formFileClass={'opacity-100 position-absolute z-n1'}
                                        />
                                    ) : (
                                        <>
                                            <PreviewAsImg
                                                pictureUrl={values[item.name]}
                                                id={item.name}
                                                width={item.width}
                                                height={item.height}
                                            />
                                            <figcaption>{formLabel(item.name)}</figcaption>
                                        </>
                                    )}
                                </Col>
                            ))}
                        </Row>

                        <Row className="mt-4">
                            {characterAndCardPictures.secondRow.map((item, i) => (
                                <Col
                                    className="d-flex flex-column align-items-center justify-content-center"
                                    style={{ gap: 10 }}
                                    key={i}
                                >
                                    {item.editable === true ? (
                                        <CLAPreviewPictureWithHOC
                                            pictureName={item.name}
                                            pictureVal={values[item.name]}
                                            resetCount={resetCount}
                                            width={item.width}
                                            maxWidth={item.maxWidth}
                                            height={item.height}
                                            heightWrapper={item.heightWrapper}
                                            wrapperClass="d-flex flex-column align-items-center justify-content-center"
                                            formFileClass={'opacity-100 position-absolute z-n1'}
                                        />
                                    ) : (
                                        <>
                                            <PreviewAsImg
                                                pictureUrl={values[item.name]}
                                                id={item.name}
                                                width={item.width}
                                                height={item.height}
                                            />
                                            <figcaption>{formLabel(item.name)}</figcaption>
                                        </>
                                    )}
                                </Col>
                            ))}
                        </Row>

                        <Row className="mt-4">
                            {characterAndCardPictures.thirdRow.map((item, i) => (
                                <Col
                                    key={i}
                                    sm={item.sm}
                                    className="d-flex flex-column align-items-center justify-content-center"
                                    style={{ gap: 10 }}
                                >
                                    {item.editable === true ? (
                                        <CLAPreviewPictureWithHOC
                                            pictureName={item.name}
                                            pictureVal={values[item.name]}
                                            resetCount={resetCount}
                                            width={item.width}
                                            height={item.height}
                                            heightWrapper={item.heightWrapper}
                                            wrapperClass="d-flex flex-column align-items-center justify-content-center"
                                            formFileClass={'opacity-100 position-absolute z-n1'}
                                        />
                                    ) : (
                                        <>
                                            <PreviewAsImg
                                                pictureUrl={values[item.name]}
                                                id={item.name}
                                                width={item.width}
                                                height={item.height}
                                            />
                                            <figcaption>{formLabel(item.name)}</figcaption>
                                        </>
                                    )}
                                </Col>
                            ))}
                        </Row>
                    </Fieldset>

                    <Form.Row>
                        <DescriptionAndClassBonus bonusConditions={bonusConditions} />
                    </Form.Row>
                    <FormGroupDeckList
                        name="decklist"
                        title="Deck"
                        headerClass="bg-info"
                        options={actions}
                    />
                </>
            )}
        </>
    );
}

const withFormCombo =
    (FormCombo) =>
    // eslint-disable-next-line react/prop-types
    ({ name, title, headerClass, options }) => {
        const renderFunc = (index, defaultValue1) => (
            <Form.Row>
                <FormSelect
                    name={`${[name]}.${index}.actionId`}
                    options={options}
                    sm={6}
                    defaultValue={defaultValue1}
                    label="Name"
                />
                <FormNumber
                    name={`${[name]}.${index}.quantity`}
                    label={'Value'}
                    sm={4}
                />
                <FormRadio name={`${[name]}.${index}.innate`} />
            </Form.Row>
        );

        return (
            <FormCombo
                sm={4}
                name={name}
                title={title}
                headerClass={headerClass}
                renderFunc={renderFunc}
                options={options}
                valueObj={{
                    actionId: '',
                    quantity: 0,
                    innate: '',
                }}
            ></FormCombo>
        );
    };
const FormGroupDeckList = withFormCombo(FormCombo);

CardInformation.propTypes = {
    values: PropTypes.object.isRequired,
};
export default CardInformation;
